import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/layout";
import Permission, { hasPermission } from "../../../_config/Permission";
import { useMenuPermissionsSelector } from "../../../store/selectors/userSelector";
import { URLS } from "../../../_config";
import Header from "../../../components/header";
import SubHeader from "../../../components/SubHeader";
import SiteButtons from "../../../components/SiteButtons";
import Container from "../../../components/Container";
import SelectBox from "../../../components/elements/form/selectBox";
import RadioButton from "../../../components/elements/form/radioButton";
import DecimalInput from "../../../components/elements/form/DecimalInput";
import Icon from "../../../components/icons";
import { useLookup, getLookupDisplayValue } from "../../../_common/hookFunction";
import { LOOKUP_VALUE } from "../../../_common/constant";
import { useAppLoader } from "../../../_common/pageLoader/appLoaderHook";
import { useSettingReturnApi } from "../../../store/api/hooks/settingsReturnApiHook";
import { useInventoryApi } from "../../../store/api/hooks/inventoryApiHook";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { roundMoney } from "../../../_common/functions";
type formValues = {
    fkRefundMethodLookupValueID: any[];
    fkShippingCostLookupValueID: string;
    fkRestockingFeeLookupValueID: string;
    enableReturn: boolean;
    shippingCost: any;
    enableRestockingFee: boolean;
    restockingFee: any;
    isReasonRequired: boolean;
    active: boolean;
    enableStoreCredit: boolean;
    returnPolicyReasons: Array<{
        returnPolicyReasonID: any;
        reasonName: string;
        active: boolean;
    }>;
};

const Return: React.FC = () => {
    const { t, i18n } = useTranslation("setting");
    const navigate = useNavigate();
    const lookup = useLookup();
    const settingReturnApi = useSettingReturnApi();
    const inventoryApi = useInventoryApi();

    const { showLoader, hideLoader } = useAppLoader();

    // Get permissions from Redux store based on current page URL using useMemo for optimization
    const allPermissions = useMenuPermissionsSelector();
    const permissionData = useMemo(() =>
        allPermissions?.[URLS.SETTINGS_RETURN] || null,
        [allPermissions]
    );
    const canWrite = useMemo(() => hasPermission(permissionData, "W"), [permissionData]);
    const { control, setValue, getValues, handleSubmit, formState: { errors }, watch, trigger, reset } = useForm<formValues>({
        defaultValues: {
            fkRefundMethodLookupValueID: [],
            fkShippingCostLookupValueID: `MjA3`,
            fkRestockingFeeLookupValueID: ``,
            enableReturn: true,
            shippingCost: `0.00`,
            enableRestockingFee: false,
            restockingFee: `0.00`,
            isReasonRequired: false,
            active: true,
            enableStoreCredit: false,
            returnPolicyReasons: []
        }
    });
    const blankReasonRow = { returnPolicyReasonID: null, reasonName: "", active: true };

    const [isStoreCreditEnabled, setIsStoreCreditEnabled] = React.useState<boolean>(true);
    const [masterdata, setMasterdata] = React.useState<any>(null);

    const returnPolicyReasons = watch("returnPolicyReasons");
    const enableReturn = watch("enableReturn");
    const enableStoreCredit = watch("enableStoreCredit");
    const enableRestockingFee = watch("enableRestockingFee");
    const fkRestockingFeeLookupValueID = watch("fkRestockingFeeLookupValueID");

    const refundMethodOptions = useMemo(() => {
        if (!masterdata?.refundMethod) return [];
        if (!enableStoreCredit) {
            const storeCreditValue = btoa(String(LOOKUP_VALUE.REFUND_METHOD_STORE_CREDIT));
            return masterdata.refundMethod.filter(
                (x: any) => x.value !== storeCreditValue
            );
        }
        return masterdata.refundMethod;
    }, [masterdata, enableStoreCredit]);

    // Remove store credit from selected refund methods when store credit is disabled
    React.useEffect(() => {
        if (!enableStoreCredit) {
            const current = getValues("fkRefundMethodLookupValueID") || [];
            const storeCreditValue = btoa(String(LOOKUP_VALUE.REFUND_METHOD_STORE_CREDIT));
            const filtered = current.filter(
                (m: any) => m.value !== storeCreditValue
            );
            if (filtered.length !== current.length) {
                setValue("fkRefundMethodLookupValueID", filtered);
            }
        }
    }, [enableStoreCredit]);

    const isBase64 = (str: string): boolean => {
        if (!str || typeof str !== "string") return false;
        // Remove whitespace and check length
        const cleaned = str.replace(/\s/g, "");
        if (!cleaned || cleaned.length % 4 !== 0) return false;
        // Base64 regex (standard, no data URI)
        const base64Regex = /^(?:[A-Za-z0-9+\/]{4})*(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;
        return base64Regex.test(cleaned);
    }

    const convertBase64String = (str: string): string => {
        if (!isBase64(str)) {
            return btoa(str);
        }
        return str;
    }

    const init_page_data = async () => {
        showLoader();
        try {
            // Fetch store credit settings
            const storeCreditResp: any = await new Promise((resolve, reject) =>
                inventoryApi.getStoreCredit({}, resolve, reject)
            ).catch(() => null);

            let storeCreditEnabled = true; // Default to true
            if (storeCreditResp && storeCreditResp?.success && storeCreditResp?.data?.items?.length > 0) {
                storeCreditEnabled = storeCreditResp.data.items[0].enableStoreCredit;
            }
            setIsStoreCreditEnabled(storeCreditEnabled);

            const masterData = {
                refundMethod: await lookup.getSetupReturnRefundMethod()
                    .map((x: any) => ({ label: getLookupDisplayValue(x, t), value: convertBase64String(x.lookupId) })),
                shippingCost: await lookup.getSetupReturnShippingCost().map((x: any) => ({ label: getLookupDisplayValue(x, t), value: convertBase64String(x.lookupId) })),
                restockingFee: await lookup.getSetupReturnRestockingFee().map((x: any) => ({ label: getLookupDisplayValue(x, t), value: convertBase64String(x.lookupId) })),
            };
            setMasterdata(masterData);

            const returndataresp: any = await new Promise((resolve, reject) => settingReturnApi.getSettingReturnPolicy({}, resolve, reject)).catch(() => null);
            if (!returndataresp || !returndataresp?.success) {
                // toast.error("Failed to load return settings.");
                return;
            }
            if (returndataresp && returndataresp?.success && returndataresp?.data) {
                var formdata = {
                    fkRefundMethodLookupValueID: returndataresp.data.returnPolicyRefundMethods && Array.isArray(returndataresp.data.returnPolicyRefundMethods)
                        ? returndataresp.data.returnPolicyRefundMethods.map((m: any) =>
                            masterData.refundMethod.find((o: any) => o.value === convertBase64String(m.fkRefundMethodLookupValueID))
                        ).filter(Boolean)
                        : [],
                    fkShippingCostLookupValueID: returndataresp.data.fkShippingCostLookupValueID,
                    fkRestockingFeeLookupValueID: returndataresp.data.fkRestockingFeeLookupValueID,
                    enableReturn: returndataresp.data.enableReturn,
                    shippingCost: returndataresp.data.shippingCost ? roundMoney(parseFloat(returndataresp.data.shippingCost)).toFixed(2) : `0.00`,
                    enableRestockingFee: returndataresp.data.enableRestockingFee,
                    restockingFee: returndataresp.data.restockingFee ? roundMoney(parseFloat(returndataresp.data.restockingFee)).toFixed(2) : `0.00`,
                    isReasonRequired: returndataresp.data.isReasonRequired ?? false,
                    active: returndataresp.data.active,
                    enableStoreCredit: storeCreditEnabled,
                    returnPolicyReasons: returndataresp.data.returnPolicyReasons && Array.isArray(returndataresp.data.returnPolicyReasons) && returndataresp.data.returnPolicyReasons.length > 0 ? returndataresp.data.returnPolicyReasons.map((o: any) => {
                        return {
                            returnPolicyReasonID: o.returnPolicyReasonID,
                            reasonName: o.reasonName,
                            active: o.active,
                        };
                    }) : [],
                };
                reset(formdata);
            }
        } finally {
            hideLoader();
        }
    };

    const onSubmit = async (data: any) => {
        console.log("Return form data:", data);
        if (data.enableReturn) {
            if (!data.fkRefundMethodLookupValueID || !Array.isArray(data.fkRefundMethodLookupValueID) || data.fkRefundMethodLookupValueID.length === 0) {
                toast.error(t("returns.errors.selectRefundMethod"));
                return;
            }
            if (data.fkShippingCostLookupValueID && data.fkShippingCostLookupValueID === `MjA4` && !data.shippingCost) {
                toast.error(t("returns.errors.specifyShippingCost"));
                return;
            }
            if (data.enableRestockingFee && !data.restockingFee) {
                toast.error(t("returns.errors.specifyRestockingFee"));
                return;
            }
            if (data.returnPolicyReasons.length > 0) {
                const errreasons = data.returnPolicyReasons.filter((o: any) => o.reasonName.trim() === ``);
                if (errreasons.length > 0) {
                    toast.error(t("returns.errors.reasonsEmpty"));
                    return;
                }

                // Check for duplicate reason names
                const reasonNames = data.returnPolicyReasons.map((o: any) => o.reasonName.trim().toLowerCase());
                const duplicates = reasonNames.filter((name: string, index: number) => reasonNames.indexOf(name) !== index);
                if (duplicates.length > 0) {
                    toast.error(t("returns.errors.duplicateReasons"));
                    return;
                }
            }
        }
        //}
        const payload = Object.assign({}, JSON.parse(JSON.stringify(data)));
        payload.fkRefundMethodLookupValueID = data.fkRefundMethodLookupValueID?.map((m: any) => parseInt(atob(m.value), 10));
        payload.shippingCost = payload.shippingCost ? +payload.shippingCost : 0.00;
        payload.restockingFee = payload.restockingFee ? +payload.restockingFee : 0.00;
        delete payload.enableStoreCredit;
        showLoader();
        // Save return policy and store credit in parallel
        const [settingreturnresp]: any = await Promise.all([
            new Promise((resolve, reject) => settingReturnApi.settingReturnInsertUpdate(payload, resolve, reject)).catch(() => null),
            new Promise((resolve, reject) => inventoryApi.insertUpdateStoreCredit({ enableStoreCredit: data.enableStoreCredit }, resolve, reject)).catch(() => null),
        ]);
        hideLoader();
        if (settingreturnresp && settingreturnresp.success === true && settingreturnresp?.data) {
            toast.success(settingreturnresp.data?.message || t("returns.success.saved"));
            init_page_data();
        } else {
            toast.error(settingreturnresp?.data && settingreturnresp.data?.message ? settingreturnresp.data?.message : t("returns.errors.saveFailed"));
        }
    };

    React.useEffect(() => {
        init_page_data();
    }, []);

    // The lookup-based option labels (refund method / shipping cost / restocking
    // fee) are translated once via getLookupDisplayValue when masterdata is built,
    // so a runtime language switch leaves them frozen in the old language -- rebuild
    // them here. The already-selected refund methods are stored as full
    // {label, value} option objects in form state (not just ids), so they also need
    // re-syncing by value; shippingCost/restockingFee store only the raw id and
    // resolve their label from masterdata at render time, so they pick up the
    // refreshed masterdata for free.
    React.useEffect(() => {
        if (!masterdata) return;
        const refreshedRefundMethod = lookup.getSetupReturnRefundMethod()
            .map((x: any) => ({ label: getLookupDisplayValue(x, t), value: convertBase64String(x.lookupId) }));
        const refreshedShippingCost = lookup.getSetupReturnShippingCost()
            .map((x: any) => ({ label: getLookupDisplayValue(x, t), value: convertBase64String(x.lookupId) }));
        const refreshedRestockingFee = lookup.getSetupReturnRestockingFee()
            .map((x: any) => ({ label: getLookupDisplayValue(x, t), value: convertBase64String(x.lookupId) }));

        setMasterdata((prev: any) => (prev ? {
            ...prev,
            refundMethod: refreshedRefundMethod,
            shippingCost: refreshedShippingCost,
            restockingFee: refreshedRestockingFee,
        } : prev));

        const currentSelected = getValues("fkRefundMethodLookupValueID");
        if (Array.isArray(currentSelected) && currentSelected.length > 0) {
            setValue(
                "fkRefundMethodLookupValueID",
                currentSelected.map((sel: any) => refreshedRefundMethod.find((o: any) => o.value === sel.value) || sel)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    return (
        <Layout>
            <Header />
            <Permission value={permissionData} check="R" page={true}>
                <div className="flex-grow overflow-auto">
                <SubHeader title={t("returns.pageTitle")} subTitle="">
                    <Permission value={permissionData} check="W">
                        <SiteButtons data={[
                            { text: t("returns.save"), type: "submit", form: "savesettingsreturnform" }
                        ]}
                        />
                    </Permission>
                </SubHeader>
                <Container className="mb-12 opacity-100">
                    <div className="flex-grow overflow-auto pb-6 pt-6">
                        <form onSubmit={handleSubmit(onSubmit)} id="savesettingsreturnform">
                            {/* Enable Returns */}
                            <div className="flex items-center gap-6 mb-8">
                                <label className="font-bold text-lg text-black block">{t("returns.enableReturns")}</label>
                                <div className="flex items-center gap-2">
                                    <Controller
                                        name="enableReturn"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex gap-0 border border-gray-200 rounded">
                                                <button type="button" disabled={!canWrite} className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tl rounded-bl focus:outline-none ${field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"} ${!canWrite ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => field.onChange(true)}>
                                                    {t("returns.yes")}
                                                </button>
                                                <button type="button" disabled={!canWrite} className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tr rounded-br focus:outline-none ${!field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"} ${!canWrite ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => field.onChange(false)}>
                                                    {t("returns.no")}
                                                </button>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Reason Required */}
                            <div className="flex items-center gap-6 mb-8">
                                <label className="font-bold text-lg text-black block">{t("returns.reasonRequired")}</label>
                                <div className="flex items-center gap-2">
                                    <Controller
                                        name="isReasonRequired"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex gap-0 border border-gray-200 rounded">
                                                <button type="button" disabled={!canWrite || !enableReturn} className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tl rounded-bl focus:outline-none ${field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"} ${!canWrite || !enableReturn ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => field.onChange(true)}>
                                                    {t("returns.yes")}
                                                </button>
                                                <button type="button" disabled={!canWrite || !enableReturn} className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tr rounded-br focus:outline-none ${!field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"} ${!canWrite || !enableReturn ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => field.onChange(false)}>
                                                    {t("returns.no")}
                                                </button>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Store Credit */}
                            <div className="flex items-center gap-6 mb-8">
                                <label className="font-bold text-lg text-black block">{t("returns.enableStoreCredit")}</label>
                                <div className="flex items-center gap-2">
                                    <Controller
                                        name="enableStoreCredit"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex gap-0 border border-gray-200 rounded">
                                                <button type="button" disabled={!canWrite || !enableReturn} className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tl rounded-bl focus:outline-none ${field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"} ${!canWrite || !enableReturn ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => field.onChange(true)}>
                                                    {t("returns.yes")}
                                                </button>
                                                <button type="button" disabled={!canWrite || !enableReturn} className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tr rounded-br focus:outline-none ${!field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"} ${!canWrite || !enableReturn ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => field.onChange(false)}>
                                                    {t("returns.no")}
                                                </button>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Refund Methods */}
                            <div className={`py-6 mb-8`}>
                                <h2 className="font-bold text-black text-lg mb-4 pb-4 border-b border-gray-400">{t("returns.refunds.heading")}</h2>
                                <div>
                                    <label className="block text-sm text-black mb-2">{t("returns.refunds.methods")}</label>
                                    <Controller
                                        name="fkRefundMethodLookupValueID"
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState }) => (
                                            <SelectBox
                                                value={value as any}
                                                onChange={onChange}
                                                isMulti={true}
                                                isClearable={true}
                                                placeholder={t("returns.refunds.selectMethod")}
                                                className={`!my-0 !mb-0 !font-normal`}
                                                options={refundMethodOptions}
                                                label={``}
                                                error={fieldState.error?.message}
                                                onDocument={true}
                                                size={`md`}
                                                mtx={true}
                                                disabled={!canWrite || !enableReturn}
                                                required={false}
                                                labelisElipsis={true}
                                                topParentStyle={{ marginTop: `0 !important`, marginBottom: `0 !important` }}
                                                topClassName={`!mb-0 !font-light outline-none`}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Shipping Cost */}
                            {/* <div className={`py-6 mb-8 ${!enableReturn ? 'opacity-50 pointer-events-none' : ''}`}> */}
                                {/* <h2 className="font-bold text-black text-lg mb-4 pb-4 border-b border-gray-400">Shipping Cost</h2> */}
                                {/* <div className="py-3">
                                    <Controller
                                        name="fkShippingCostLookupValueID"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex flex-col md:flex-row gap-7">
                                                <RadioButton
                                                    checked={field.value === "MjA3"}
                                                    onChange={() => field.onChange("MjA3")}
                                                    label={<span className="text-black text-[15px]">Merchant Return Shipping Costs</span>}
                                                />
                                                <div className="flex items-center justify-evenly grow gap-2">
                                                    <RadioButton
                                                        checked={field.value === "MjA4"}
                                                        onChange={() => field.onChange("MjA4")}
                                                        label={<span className="text-black text-[15px]">Customer Fixed Return Fee</span>}
                                                    />
                                                    <div className="relative">
                                                        <Controller
                                                            name="shippingCost"
                                                            control={control}
                                                            render={({ field: feeField }) => (
                                                                <DecimalInput
                                                                    {...feeField}
                                                                    disabled={field.value !== "MjA4"}
                                                                    className="ml-2 w-36"
                                                                    placeholder="5.00"
                                                                    fieldType="amount"
                                                                    decimalPlaces={2}
                                                                    min={0}
                                                                    max={9999999999}
                                                                />
                                                            )}
                                                        />
                                                        <span className="ml-1 text-gray-400 text-xl absolute right-2 top-4 font-light">$</span>
                                                    </div>
                                                </div>
                                                <RadioButton
                                                    checked={field.value === "MjA5"}
                                                    onChange={() => field.onChange("MjA5")}
                                                    label={<span className="text-black">Customer Own Courier</span>}
                                                />
                                            </div>
                                        )}
                                    />
                                </div> */}
                            {/* </div> */}

                            {/* Restocking Fee */}
                            {/* <div className={`py-6 mb-8 ${!enableReturn ? 'opacity-50 pointer-events-none' : ''}`}> */}
                                {/* <div className="flex justify-between items-center border-b border-gray-400 pb-4 mb-4"> */}
                                    {/* <h2 className="font-bold text-black text-lg flex items-center">
                                        <span>Restocking Fee</span>
                                        <span className="font-bold text-sm ml-2">*</span> */}
                                        {/* only show this icon and tooltip when percentage is selected */}
                                        {/* {fkRestockingFeeLookupValueID && fkRestockingFeeLookupValueID !== `MjEw` && (
                                            <span className="ml-2 inline-flex items-center">
                                                <Icon name="info" className="text-secondary cursor-pointer" title={"Restocking Fee Percentage Is Based On Retail Price Excluding Taxes"} />
                                            </span>
                                        )}
                                    </h2> */}
                                    {/* Just put the control name you will automatecally get and set the true/false */}
                                    {/* <Controller
                                        name="enableRestockingFee"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex gap-0 border border-gray-200 rounded">
                                                <button type="button" className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tl rounded-bl focus:outline-none ${field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"}`} onClick={() => field.onChange(true)}>
                                                    {t("returns.yes")}
                                                </button>
                                                <button type="button" className={`px-4 min-w-[53px] h-[35px] text-[13px] whitespace-nowrap flex items-center justify-center rounded-tr rounded-br focus:outline-none ${!field.value ? "bg-secondary text-white" : "bg-gray-50 text-gray-900"}`} onClick={() => field.onChange(false)}>
                                                    {t("returns.no")}
                                                </button>
                                            </div>
                                        )}
                                    /> */}
                                {/* </div> */}
                                {/* <div className={!enableRestockingFee ? 'opacity-50 pointer-events-none' : ''}>
                                    <Controller
                                        name="fkRestockingFeeLookupValueID"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="grid grid-cols-2 gap-8">
                                                {masterdata && Array.isArray(masterdata.restockingFee) && masterdata.restockingFee.map((feeOption: any, index: number) => (
                                                    <div className="flex items-center justify-between grow gap-4" key={`restockfee-${index}`}>
                                                        <RadioButton
                                                            checked={field.value === feeOption.value}
                                                            onChange={() => {
                                                                field.onChange(feeOption.value);
                                                                setValue("restockingFee", `0`);
                                                            }}
                                                            label={<span className="text-black text-[15px]">{feeOption.label}</span>}
                                                        />
                                                        <div className="relative">
                                                            {field.value !== feeOption.value ? (
                                                                <DecimalInput
                                                                    disabled={true}
                                                                    className="ml-2 w-64"
                                                                    placeholder="0.00"
                                                                    fieldType="amount"
                                                                    decimalPlaces={2}
                                                                    min={0}
                                                                    max={9999999999}
                                                                    value=""
                                                                />
                                                            ) : (
                                                                <Controller
                                                                    name="restockingFee"
                                                                    control={control}
                                                                    disabled={field.value !== feeOption.value}
                                                                    render={({ field: feeField }) => (
                                                                        <DecimalInput
                                                                            {...feeField}
                                                                            disabled={field.value !== feeOption.value}
                                                                            className="ml-2 w-64"
                                                                            placeholder="0.00"
                                                                            fieldType="amount"
                                                                            decimalPlaces={2}
                                                                            min={0}
                                                                            max={feeOption.value !== `MjEw` ? 100 : 9999999999}
                                                                        />
                                                                    )}
                                                                />
                                                            )}
                                                            {feeOption.value === `MjEw` ? (
                                                                <span className="ml-1 text-gray-400 text-xl absolute right-2 top-4 font-light">$</span>
                                                            ) : (
                                                                <span className="ml-1 text-gray-400 text-xl absolute right-2 top-4 font-light">%</span>
                                                            )}

                                                        </div>
                                                    </div>
                                                ))}

                                            </div>
                                        )}
                                    />
                                </div> */}
                            {/* </div> */}

                            

                            {/* Return Reasons */}
                            <div className={`py-6 mb-6 ${/*!enableReturn ? 'opacity-50 pointer-events-none' : ''*/ + ``}`}>
                                <h2 className="font-bold text-black text-lg mb-6 pb-4 border-b border-gray-400">{t("returns.reasons.heading")}</h2>
                                <div>
                                    {Array.isArray(returnPolicyReasons) && returnPolicyReasons.length > 0 && returnPolicyReasons.map((reason, index) => (
                                        <div className="flex justify-between items-center gap-8 mb-3" key={`reasonrow-${index}`}>
                                            <Controller
                                                name={`returnPolicyReasons.${index}.reasonName`}
                                                control={control}
                                                render={({ field }) => {
                                                    const currentReasons = getValues("returnPolicyReasons") || [];
                                                    const currentValue = field.value?.trim().toLowerCase();
                                                    const isDuplicate = currentValue && currentReasons.some((r: any, i: number) => 
                                                        i !== index && r.reasonName?.trim().toLowerCase() === currentValue
                                                    );
                                                    
                                                    return (
                                                        <div className="w-full">
                                                            <input
                                                                type="text"
                                                                {...field}
                                                                disabled={!canWrite || !enableReturn}
                                                                className={`w-full border rounded-md px-3 py-3 text-black text-sm focus:outline-none placeholder:font-light ${
                                                                    isDuplicate ? 'border-red-500' : 'border-gray-300'
                                                                } ${!canWrite || !enableReturn ? 'cursor-not-allowed' : ''}`}
                                                                placeholder={t("returns.reasons.placeholder")}
                                                            />
                                                            {isDuplicate && (
                                                                <p className="text-red-500 text-xs mt-1 mb-1">{t("returns.reasons.duplicateInline")}</p>
                                                            )}
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <button type="button" disabled={!canWrite || !enableReturn} className={`text-red-500 hover:text-red-700 ${!canWrite || !enableReturn ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={() => {
                                                const currentReasons = getValues("returnPolicyReasons") || [];
                                                if (currentReasons.length <= 1) {
                                                    toast.error(t("returns.errors.lastReason"));
                                                    return;
                                                }
                                                currentReasons.splice(index, 1);
                                                setValue("returnPolicyReasons", [...currentReasons]);
                                            }}>
                                                <Icon name={`delete`} size={22} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <a className={`bg-secondary text-white w-14 h-12 flex items-center justify-center rounded-md ${!canWrite || !enableReturn ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#bf6e73] cursor-pointer'}`} onClick={() => {
                                    if (!canWrite || !enableReturn) return;
                                    const newReason = { ...blankReasonRow };
                                    const currentReasons = getValues("returnPolicyReasons") || [];
                                    setValue("returnPolicyReasons", [...currentReasons, newReason]);
                                }}>
                                    <Icon name={`plus`} size={24} />
                                </a>
                            </div>
                        </form>
                    </div>
                </Container>
                </div>
            </Permission>
        </Layout>
    )
}

export default Return;